import { ContactItem } from "@/components/contacts/ContactItem.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import type { Contact } from "@/types/contact.ts";
import { Search } from "lucide-react";
import { useState } from "react";

interface MergeContactDialogProps {
  sourceContact: Contact | null;
  availableContacts: Contact[];
  onMerge: (sourceContactId: string, targetContactId: string) => Promise<void>;
  onCancel: () => void;
  isMerging: boolean;
}

/**
 * Dialog for merging contacts. Shows the source contact on the left
 * and allows searching for a target contact to merge into.
 */
export const MergeContactDialog = ({
  sourceContact,
  availableContacts,
  onMerge,
  onCancel,
  isMerging,
}: MergeContactDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTargetContact, setSelectedTargetContact] = useState<Contact | null>(null);

  const handleMergeConfirm = async () => {
    if (!sourceContact || !selectedTargetContact) return;
    await onMerge(sourceContact.id, selectedTargetContact.id);
  };

  // Filter available contacts to exclude the source contact and apply search
  const filteredTargetContacts = availableContacts.filter((contact) => {
    // Exclude source contact and user/assistant contacts
    if (contact.id === sourceContact?.id) return false;
    if (contact.type === "user" || contact.type === "assistant") return false;
    if (contact.mergedInto) return false; // Exclude already merged contacts

    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    // Search by name
    if (contact.name?.toLowerCase().includes(query)) return true;

    // Search by email or phone number
    if (contact.handles?.some((handle) => handle.value.toLowerCase().includes(query))) {
      return true;
    }

    return false;
  });

  return (
    <Dialog open={sourceContact !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="flex flex-col mx-auto max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>Merge Contacts</DialogTitle>
          <DialogDescription>
            Select a contact to merge <strong>{sourceContact?.name}</strong> into. All handles
            (emails and phone numbers) will be transferred to the target contact.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col md:flex-row gap-4 max-h-[50vh] overflow-y-auto flex-1 min-h-0">
          {/* Left side: Source contact */}
          <div className="flex-1 border rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Merging from:</h3>
            {sourceContact && (
              <div className="mb-4">
                <ContactItem contact={sourceContact} />
                {sourceContact.handles && sourceContact.handles.length > 0 && (
                  <div className="mt-3 pl-4 text-sm text-muted-foreground">
                    <div className="font-medium mb-1">Handles to transfer:</div>
                    <ul className="list-disc pl-4 space-y-1">
                      {sourceContact.handles.map((handle) => (
                        <li key={handle.id}>{handle.value}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right side: Target contact selection */}
          <div className="flex-1 border rounded-lg p-4 flex flex-col">
            <h3 className="text-sm font-medium mb-3">Merge into:</h3>

            {/* Search input */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Contact list */}
            <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
              {filteredTargetContacts.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {searchQuery.trim()
                    ? "No matching contacts found"
                    : "No available contacts to merge into"}
                </div>
              ) : (
                filteredTargetContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className={`cursor-pointer rounded-lg border-2 transition-colors ${
                      selectedTargetContact?.id === contact.id
                        ? "border-primary bg-primary/5"
                        : "border-transparent hover:border-muted"
                    }`}
                    onClick={() => setSelectedTargetContact(contact)}
                  >
                    <ContactItem contact={contact} />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isMerging}
            data-statsig-name="contacts-merge-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMergeConfirm}
            disabled={!selectedTargetContact || isMerging}
            data-statsig-name="contacts-merge-confirm"
          >
            {isMerging ? "Merging..." : "Merge Contacts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
